import { styled } from '@mui/material';
import { Stack } from '@mui/system';
import { useMemo } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import Iconify from 'src/components/iconify';
import { BoardAvatar } from 'src/features/board/BoardAvatar';
import { ChannelAvatar } from 'src/features/channel/ChannelAvatar';
import { P2PChannelAvatar } from 'src/features/channel/P2PChannelAvatar';
import { DocAvatar } from 'src/features/doc/DocAvatar';
import { TodoListAvatar } from 'src/features/todo/TodoListAvatar';
import { UserRecentFeature } from 'src/generated/graphql';
import { UserRecent } from 'src/hooks/recents';
import {
  useWorkspaceChannel,
  useWorkspaceParticipantsUsers,
} from 'src/hooks/useWorkspace';
import { iconByUrl } from 'src/sections/@dashboard/recents/shared';

type NavRecentItemProps = {
  recent: UserRecent;
};

export const NavRecentItemIcon = ({ recent }: NavRecentItemProps) => {
  const { user } = useAuthContext();
  const conversation = useWorkspaceChannel(recent.objectRef || '');

  const participants = useWorkspaceParticipantsUsers(
    conversation?.participants || [],
  );
  const users = useMemo(
    () => participants?.filter((i) => i._id !== user?._id),
    [participants, user?._id],
  );
  const otherUser = useMemo(() => users[0], [users]);
  const recentsIconContent = useMemo(() => {
    if (recent.feature === UserRecentFeature.AskAi) {
      return <Iconify icon="copera:ai" />;
    }
    if (
      conversation &&
      (recent.feature === UserRecentFeature.TextChannel ||
        recent.feature === UserRecentFeature.VoiceChannel)
    ) {
      return (
        <Stack direction="row" justifyContent="center" width={20}>
          <ChannelAvatar key={conversation?._id} channel={conversation} />
        </Stack>
      );
    }
    if (recent.feature === UserRecentFeature.ThreadMessage) {
      return <Iconify icon="tabler:timeline-event-text" />;
    }
    if (otherUser && recent.feature === UserRecentFeature.DirectMessage) {
      return <P2PChannelAvatar otherUser={otherUser} />;
    }
    if (
      recent.feature === UserRecentFeature.BoardRow ||
      recent.feature === UserRecentFeature.BoardView
    ) {
      return <BoardAvatar emoji={recent.icon} />;
    }
    if (
      recent.feature === UserRecentFeature.TodoItem ||
      recent.feature === UserRecentFeature.TodoList
    ) {
      return (
        <TodoListAvatar
          todo={{ name: recent.title || '', icon: recent.icon }}
        />
      );
    }
    if (recent.feature === UserRecentFeature.Docs) {
      return <DocAvatar icon={recent.icon} />;
    }
    if (
      recent.feature === UserRecentFeature.DriveFile ||
      recent.feature === UserRecentFeature.DriveFolder
    ) {
      return <Iconify icon={iconByUrl[recent.url]} />;
    }
  }, [recent, conversation, otherUser]);

  return <StyledRecentsIcon>{recentsIconContent}</StyledRecentsIcon>;
};

const StyledRecentsIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 'unset !important',
}));
